import { isDropoffPhaseStatus, isTerminalTaskStatus, normalizeTaskStatus, TASK_STATUS } from "../constants/operations";

export function driverNameOf(task) {
  return task?.assignedDriver || task?.driver || "";
}

export function hasAssignedDriver(task) {
  const name = String(driverNameOf(task) || "").trim().toLowerCase();
  return !!name && name !== "unassigned";
}

export function isTaskAssignedToDriver(task, driver) {
  if (!task || !driver) return false;
  const driverId = String(driver.id || "").trim();
  const assignedDriverId = String(task.assigned_driver_id || "").trim();
  if (driverId && assignedDriverId && driverId === assignedDriverId) {
    return true;
  }

  const driverName = String(driver.name || "").trim().toLowerCase();
  if (!driverName) return false;
  return [task.assignedDriver, task.driver]
    .map((value) => String(value || "").trim().toLowerCase())
    .includes(driverName);
}

export function isEmergencyTask(task) {
  return (
    task?.isEmergency === true ||
    String(task?.status || "").toLowerCase().includes("emergency") ||
    String(task?.notes || "").startsWith("STAT / TRAUMA")
  );
}

export function getDeadlineIso(task) {
  return task?.deliveryDeadline || task?.milestones?.deliveryDeadline || task?.deliverBy || null;
}

export function getTaskRisk(task, nowMs = Date.now()) {
  const raw = getDeadlineIso(task);
  if (!raw) return { level: null, minsLeft: null, label: "No deadline", sortWeight: 99 };

  const minsLeft = (new Date(raw).getTime() - nowMs) / 60000;
  if (minsLeft < 0) return { level: "Late", minsLeft, label: "Overdue", sortWeight: 0 };
  if (minsLeft < 30) return { level: "Critical", minsLeft, label: `${Math.round(minsLeft)}m left`, sortWeight: 1 };
  if (minsLeft < 120) return { level: "High", minsLeft, label: `${Math.round(minsLeft)}m left`, sortWeight: 2 };
  if (minsLeft < 240) return { level: "Medium", minsLeft, label: `${Math.round(minsLeft)}m left`, sortWeight: 3 };
  return { level: "Low", minsLeft, label: `${Math.round(minsLeft / 60)}h left`, sortWeight: 4 };
}

export function isReassignmentRequired(task) {
  return (
    task?.reassignmentRequired === true ||
    String(task?.reassignmentReason || "").trim().toLowerCase() === "driver_removed"
  );
}

export function reassignmentMessage(task) {
  if (!isReassignmentRequired(task)) return "";
  return task?.reassignmentMessage || "DRIVER REMOVED — REASSIGN IMMEDIATELY";
}

export function getOverrideMeta(task, type) {
  const nested = task?.overrides?.[type];
  const fallbackMap = {
    arrival: task?.arrivalOverrideState,
    delivery: task?.deliveryOverrideState,
    pickup: task?.pickupOverrideState,
  };
  const fallbackState = fallbackMap[type];
  if (nested && typeof nested === "object") {
    return { type, ...nested, state: nested.state || fallbackState || "none" };
  }
  return { type, state: fallbackState || "none" };
}

export function getPendingOverride(task) {
  const pickup = getOverrideMeta(task, "pickup");
  if (pickup.state === "pickup_override_requested") return pickup;
  const arrival = getOverrideMeta(task, "arrival");
  if (arrival.state === "arrival_override_requested") return arrival;
  const delivery = getOverrideMeta(task, "delivery");
  if (delivery.state === "delivery_override_requested") return delivery;
  return null;
}

export function locationStatusInfo(status) {
  const map = {
    geocoded: {
      label: "GEOCODED",
      chipColor: "success",
      severity: null,
      message: null,
    },
    manual_override: {
      label: "MANUAL",
      chipColor: "info",
      severity: null,
      message: null,
    },
    geocode_failed: {
      label: "GEOCODE FAILED",
      chipColor: "warning",
      severity: "warning",
      message: "Location could not be verified — driver completion will be blocked.",
    },
    geocode_approximate: {
      label: "APPROXIMATE",
      chipColor: "warning",
      severity: "warning",
      message:
        "Only a street- or area-level point was found, so it is not enforced — the driver will be blocked until you confirm the exact point.",
    },
    no_address: {
      label: "NO ADDRESS",
      chipColor: "error",
      severity: "error",
      message: "No delivery address on file — driver completion will be blocked.",
    },
  };
  return (
    map[status] || {
      label: "UNVERIFIED",
      chipColor: "warning",
      severity: "warning",
      message: "Location unverified — driver completion will be blocked.",
    }
  );
}

const LOCATION_PROBLEM_STATUSES = new Set(["geocode_failed", "geocode_approximate", "no_address"]);
const LOCATION_OK_STATUSES = new Set(["geocoded", "manual_override"]);

/**
 * Status / candidate field names per stop. The dropoff keeps the original
 * unprefixed `locationStatus`; the pickup (previously unflagged) uses
 * `pickupLocationStatus`.
 */
export const STOP_FIELDS = Object.freeze({
  dropoff: { label: "Delivery", lat: "dropoffLat", lng: "dropoffLng", address: "dropoffAddress", status: "locationStatus", candidate: "locationCandidate" },
  pickup:  { label: "Pickup",   lat: "pickupLat",  lng: "pickupLng",  address: "pickupAddress",  status: "pickupLocationStatus", candidate: "pickupLocationCandidate" },
});

function stopHasCoords(task, stop) {
  const f = STOP_FIELDS[stop];
  const num = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));
  return num(task?.[f.lat]) && num(task?.[f.lng]);
}

function stopHasIssue(task, stop) {
  const s = task?.[STOP_FIELDS[stop].status];
  if (LOCATION_PROBLEM_STATUSES.has(s)) return true;
  if (LOCATION_OK_STATUSES.has(s)) return false;
  return !stopHasCoords(task, stop);
}

/**
 * Stops whose stored coordinates would block (or mis-fence) the driver.
 * The pickup only counts while pickup is still ahead (not after Picked Up /
 * terminal); the delivery counts until the task is finished.
 * Returns [{ stop: "pickup"|"dropoff", status, label }].
 */
export function getLocationIssues(task) {
  const issues = [];
  if (!task) return issues;
  const statusKey = task.status;
  if (!isDropoffPhaseStatus(statusKey) && !isTerminalTaskStatus(statusKey) && stopHasIssue(task, "pickup")) {
    issues.push({ stop: "pickup", status: task.pickupLocationStatus, label: STOP_FIELDS.pickup.label });
  }
  if (stopHasIssue(task, "dropoff")) {
    issues.push({ stop: "dropoff", status: task.locationStatus, label: STOP_FIELDS.dropoff.label });
  }
  return issues;
}

export function hasLocationIssue(task) {
  return getLocationIssues(task).length > 0;
}

const CREATE_PROBLEM_TEXT = {
  geocode_failed: "could not be located",
  geocode_approximate: "was only found approximately",
  no_address: "has no address",
};

/**
 * Warning text for the POST /tasks/ response when a stop could not be placed
 * precisely enough to geofence (so dispatch can fix it before the driver is
 * blocked in the field). Returns "" when both stops are fine.
 */
export function describeCreateLocationWarnings(created) {
  const parts = [];
  const pickup = CREATE_PROBLEM_TEXT[created?.pickupLocationStatus];
  const dropoff = CREATE_PROBLEM_TEXT[created?.locationStatus];
  if (pickup) parts.push(`The pickup address ${pickup}`);
  if (dropoff) parts.push(`The delivery address ${dropoff}`);
  if (!parts.length) return "";
  return `${parts.join(" and ")}. The driver will be blocked at arrival until dispatch confirms the location (Courier Dashboard → Fix Location).`;
}

/** "Pickup: GEOCODE FAILED · Delivery: APPROXIMATE" for queue rows. */
export function locationIssueSummary(task) {
  return getLocationIssues(task)
    .map((i) => `${i.label}: ${locationStatusInfo(i.status).label}`)
    .join(" · ");
}

export function taskIdentity(task) {
  const client = task?.clientName || task?.dropoffFacilityName || task?.hospitalName || "Unnamed task";
  const pickup = task?.pickupFacilityName || task?.pickupAddress || "Pickup";
  const dropoff = task?.dropoffFacilityName || task?.dropoffAddress || "Dropoff";
  return {
    title: client,
    route: `${pickup} → ${dropoff}`,
  };
}

export function nextDestination(task) {
  if (!task) return "—";
  return isDropoffPhaseStatus(task.status)
    ? (task.dropoffFacilityName || task.dropoffAddress || "Dropoff")
    : (task.pickupFacilityName || task.pickupAddress || "Pickup");
}

export function getTaskLastUpdateIso(task) {
  return (
    task?.reassignmentRequiredAt ||
    task?.updatedAt ||
    task?.updated_at ||
    task?.modifiedAt ||
    task?.lastUpdated ||
    task?.deliveredAt ||
    getPendingOverride(task)?.requested_at ||
    task?.createdAt ||
    null
  );
}

// The backend stores timestamps via Python's datetime.utcnow().isoformat(),
// which produces strings like "2026-03-24T23:19:00.123456" — no trailing "Z".
// JavaScript's Date constructor treats an ISO string without a timezone offset
// as LOCAL time, so the raw UTC hour is displayed verbatim instead of being
// converted to the browser's local timezone.  Appending "Z" before parsing
// forces correct UTC→local conversion.
function _toUTC(iso) {
  if (!iso) return iso;
  const s = String(iso);
  // Already has a timezone offset ("Z", "+HH:MM", "-HH:MM") — leave it alone.
  if (s.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(s)) return s;
  return s + "Z";
}

export function relativeTime(iso, nowMs = Date.now()) {
  if (!iso) return "—";
  const timestamp = new Date(_toUTC(iso)).getTime();
  if (Number.isNaN(timestamp)) return "—";
  const secAgo = Math.round((nowMs - timestamp) / 1000);
  if (secAgo < 10) return "just now";
  if (secAgo < 60) return `${secAgo}s ago`;
  if (secAgo < 3600) return `${Math.round(secAgo / 60)}m ago`;
  return new Date(_toUTC(iso)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function formatAbsoluteTime(iso) {
  if (!iso) return "—";
  const timestamp = new Date(_toUTC(iso)).getTime();
  if (Number.isNaN(timestamp)) return "—";
  return new Date(timestamp).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function isStaleDriver(updatedAt, minutes = 5, nowMs = Date.now()) {
  if (!updatedAt) return true;
  const stamp = new Date(_toUTC(updatedAt)).getTime();
  if (Number.isNaN(stamp)) return true;
  return nowMs - stamp > minutes * 60 * 1000;
}

export function staleSeverity(updatedAt, nowMs = Date.now()) {
  if (!updatedAt) return "critical";
  const stamp = new Date(_toUTC(updatedAt)).getTime();
  if (Number.isNaN(stamp)) return "critical";
  const mins = (nowMs - stamp) / 60000;
  if (mins >= 10) return "critical";
  if (mins >= 5) return "warning";
  if (mins >= 2) return "watch";
  return "healthy";
}

export function isOnShift(driver) {
  const status = normalizeTaskStatus(driver?.operational_status || driver?.current_status || "");
  return !!driver?.updated_at && status !== normalizeTaskStatus("offline");
}

export function getCurrentTaskForDriver(driver, tasks) {
  if (!driver) return null;
  const byId = driver.current_task_id
    ? tasks.find((task) => task.id === driver.current_task_id)
    : null;
  if (byId) return byId;

  return tasks.find((task) => {
    if (isTerminalTaskStatus(task.status)) return false;
    return isTaskAssignedToDriver(task, driver);
  }) || null;
}

export function getAssignedTasksForDriver(driver, tasks) {
  if (!driver) return [];
  return tasks.filter((task) => !isTerminalTaskStatus(task.status) && isTaskAssignedToDriver(task, driver));
}

export function driverHealthFlag(driver, currentTask, nowMs = Date.now()) {
  if (getPendingOverride(currentTask)) return "Override pending";
  if (isReassignmentRequired(currentTask)) return "Blocked";
  if (currentTask && hasLocationIssue(currentTask)) return "Blocked";
  if (isStaleDriver(driver?.updated_at, 5, nowMs)) return "Stale";
  if (isOnShift(driver) && !currentTask) return "No task";
  return "Healthy";
}

export function isDriverInTransit(driver, currentTask) {
  const status = normalizeTaskStatus(driver?.operational_status || driver?.current_status || "");
  if (
    [
      normalizeTaskStatus(TASK_STATUS.EN_ROUTE),
      "en route pickup",
      "at pickup",
      "in transit",
      "en route delivery",
      normalizeTaskStatus(TASK_STATUS.ARRIVED),
      normalizeTaskStatus(TASK_STATUS.PICKED_UP),
    ].includes(status)
  ) {
    return true;
  }
  return !!currentTask && isDropoffPhaseStatus(currentTask.status);
}
